/**
 * Sharing primitives: channel intent URLs, clipboard, native share, image
 * download, and the copy templates that wrap an idea report or a leaderboard
 * rank into something a friend actually wants to open.
 *
 * Links currently point at in-app routes, so a recipient who is not a Vaagon
 * user lands on login/signup. The fast follow is a public read-only report
 * page (needs a backend endpoint for unauthenticated report data); once that
 * exists, only `ideaShareUrl` below needs to change.
 */

export type ShareChannel =
  | 'whatsapp'
  | 'telegram'
  | 'linkedin'
  | 'facebook'
  | 'email'
  | 'instagram'
  | 'copy'
  | 'native'

/** What the user is sharing. Drives both the card art and the copy. */
export type ShareSubject =
  | {
      kind: 'idea'
      ideaId: string
      title: string
      oneLinePitch?: string
      overallScore: number | null
      scoreLabel: string
      votes: number
      rank?: number | null
    }
  | {
      kind: 'rank'
      rank: number
      totalValidators: number
      /** Optional: when present the card greets the user by name. */
      userName?: string
      points?: number
    }

export type SharePayload = {
  /** Plain-text message, already includes nothing about the URL. */
  text: string
  /** Absolute URL the share points at. */
  url: string
  /** Short title for native share / email subject. */
  title: string
}

/** Origin the app is served from. window.location keeps dev and prod correct. */
export function appBaseUrl(): string {
  return window.location.origin
}

export function ideaShareUrl(ideaId: string): string {
  // Recipients land on the public validation page (not the owner-only report),
  // so anyone who opens a shared idea can validate it.
  return `${appBaseUrl()}/validate-ideas/${ideaId}`
}

export function leaderboardShareUrl(): string {
  return `${appBaseUrl()}/leaderboard`
}

/** Builds the message + url + title for a subject. */
export function buildSharePayload(subject: ShareSubject): SharePayload {
  if (subject.kind === 'idea') {
    const scoreBit =
      subject.overallScore != null
        ? ` It is scoring ${subject.overallScore}% (${subject.scoreLabel}) so far`
        : ''
    const rankBit =
      subject.rank != null ? ` and ranks #${subject.rank} on Vaagon` : ''
    return {
      title: `${subject.title} on Vaagon`,
      url: ideaShareUrl(subject.ideaId),
      text: `I am validating my idea "${subject.title}" on Vaagon.${scoreBit}${rankBit}. Take a look and tell me what you think.`,
    }
  }
  const pointsBit =
    subject.points != null
      ? ` with ${subject.points.toLocaleString()} community points`
      : ''
  return {
    title: `#${subject.rank} on the Vaagon leaderboard`,
    url: leaderboardShareUrl(),
    text: `I am ranked #${subject.rank} out of ${subject.totalValidators.toLocaleString()} validators on Vaagon${pointsBit}. Come validate ideas and climb the leaderboard with me.`,
  }
}

/**
 * Web share-intent URL for a channel, or null when the channel has no web
 * intent (Instagram, copy, native) and must be handled another way.
 */
export function channelIntentUrl(
  channel: ShareChannel,
  payload: SharePayload,
): string | null {
  const text = encodeURIComponent(payload.text)
  const url = encodeURIComponent(payload.url)
  const textWithUrl = encodeURIComponent(`${payload.text}\n\n${payload.url}`)

  switch (channel) {
    case 'whatsapp':
      return `https://wa.me/?text=${textWithUrl}`
    case 'telegram':
      return `https://t.me/share/url?url=${url}&text=${text}`
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`
    case 'email':
      return `mailto:?subject=${encodeURIComponent(payload.title)}&body=${textWithUrl}`
    default:
      return null
  }
}

/** Opens a channel intent in a new tab (or the mail client for email). */
export function openChannel(
  channel: ShareChannel,
  payload: SharePayload,
): void {
  const intent = channelIntentUrl(channel, payload)
  if (!intent) return
  if (channel === 'email') {
    window.location.href = intent
    return
  }
  window.open(intent, '_blank', 'noopener,noreferrer')
}

/** Copies text to the clipboard. Returns false if the API is unavailable. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

/** True when the browser can open the OS share sheet. */
export function canNativeShare(): boolean {
  return (
    typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  )
}

/** True when the browser can share files (image) through the OS share sheet. */
export function canNativeShareFiles(files: File[]): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files })
  )
}

/**
 * Opens the OS share sheet. Returns false if the user dismissed it or it is
 * unsupported, true on a completed share.
 */
export async function nativeShare(data: {
  title: string
  text: string
  url?: string
  files?: File[]
}): Promise<boolean> {
  if (!canNativeShare()) return false
  try {
    await navigator.share(data)
    return true
  } catch {
    // AbortError when the user dismisses the sheet; treat as a no-op.
    return false
  }
}

/** Triggers a browser download of a data URL. */
export function downloadDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement('a')
  a.href = dataUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/** Converts a data URL into a File so it can ride the native share sheet. */
export async function dataUrlToFile(
  dataUrl: string,
  filename: string,
): Promise<File> {
  const res = await fetch(dataUrl)
  const blob = await res.blob()
  return new File([blob], filename, { type: blob.type || 'image/png' })
}

/** Filesystem-safe slug for download filenames. */
export function shareFilename(subject: ShareSubject): string {
  if (subject.kind === 'idea') {
    const slug = subject.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48)
    return `vaagon-${slug || 'idea'}.png`
  }
  return `vaagon-rank-${subject.rank}.png`
}
